import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

/**
 * The client's spendable store balance at this business.
 *
 * This deliberately lives in a hook owned by the booking flow rather than
 * inside GuestCreditBadge, which is where the lookup used to be. The badge
 * only renders on step 1, so the lookup was unmounted the moment the client
 * pressed Continue -- and its 300ms debounce was cancelled by the unmount
 * cleanup before it ever fired. A client who typed their number and moved on
 * quickly reached the payment step with a balance of 0, so the "use my store
 * balance" control never appeared. Stepping back to page 1 and waiting made it
 * work, which is exactly the "go back, then forward, and now it's there"
 * behaviour that was reported.
 *
 * Owning it at the flow level means the balance is fetched once for the phone
 * number and survives every step change.
 */
export function useGuestCredit(businessId: string | undefined, phone: string) {
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const cleanPhone = phone?.replace(/\D/g, "") || "";
    if (!businessId || cleanPhone.length < 9) {
      setBalance(0);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc("get_guest_salon_credit", {
          p_business_id: businessId,
          p_phone: phone,
        });
        if (error) throw error;
        if (isMounted) setBalance(Number(data?.balance) || 0);
      } catch (err) {
        console.error("Error fetching guest credit balance:", err);
        if (isMounted) setBalance(0);
      } finally {
        if (isMounted) setLoading(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [businessId, phone]);

  return { balance, loading };
}
